/**
 * Push-to-talk hotkey encoding.
 *
 * A binding is stored as `Modifier+Modifier+KeyboardEvent.code`, e.g.
 * `Alt+KeyV` or `Space`. `code` and not `key`: push-to-talk must work on a
 * Devanagari or AZERTY layout, and `key` changes with the layout while the
 * physical key does not.
 */

const MODIFIER_CODES = new Set(['ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight',
  'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight']);

export const DEFAULT_PUSH_TO_TALK = 'Space';

export interface HotkeyEventLike {
  code: string;
  altKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  metaKey: boolean;
}

/** Builds a binding string from a keydown. Returns null for a bare modifier. */
export function hotkeyFromEvent(event: HotkeyEventLike): string | null {
  if (MODIFIER_CODES.has(event.code)) return null;
  const parts: string[] = [];
  if (event.ctrlKey) parts.push('Ctrl');
  if (event.altKey) parts.push('Alt');
  if (event.shiftKey) parts.push('Shift');
  if (event.metaKey) parts.push('Meta');
  parts.push(event.code);
  return parts.join('+');
}

export function matchesHotkey(event: HotkeyEventLike, binding: string): boolean {
  const parts = binding.split('+');
  const code = parts[parts.length - 1];
  if (code === undefined || event.code !== code) return false;
  return (
    parts.includes('Ctrl') === event.ctrlKey &&
    parts.includes('Alt') === event.altKey &&
    parts.includes('Shift') === event.shiftKey &&
    parts.includes('Meta') === event.metaKey
  );
}

/** Human label: `Alt+KeyV` → `Alt + V`, `Space` → `Space`. */
export function formatHotkey(binding: string): string {
  return binding
    .split('+')
    .map((part) => part.replace(/^Key/, '').replace(/^Digit/, '').replace(/^Arrow/, ''))
    .join(' + ');
}
